import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import { MdOutlineMedicalServices } from "react-icons/md";
import drEasyApi from '../../Api/Customapi';
import { useDoctorQuery } from '../../hooks/useDoctorQuery';
import { getImg } from '../../Api/getImg';
import imgBg from "../../assets/serviceBg3.png";
import { useDoctorContext } from '../../context/DoctorContext';
import { useLanguage } from '../../context/LanguageContext';

const containerVariants = {
    hidden: {},
    visible: {
        transition: {
            staggerChildren: 0.2,
        },
    },
};

const cardVariants = {
    hidden: { opacity: 0, y: -30 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.6, ease: 'easeOut' },
    },
};

export default function OurServices() {
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const { data: doctor } = useDoctorQuery();
    const { setHasServices } = useDoctorContext();
    const { slug } = useParams();
    const { t, currentLanguage } = useLanguage();

    const navigate = useNavigate();

    const url = window.location.href;
    const currentPath = url.substring(url.lastIndexOf('/') + 1);
    
    // Check if current path is "services" to show all services
    const isServicesPage = currentPath === "services";

    useEffect(() => {
        const fetchServices = async () => {
            try {
                const res = await drEasyApi.get('/api/Services/GetDoctorServices', {
                    params: { DoctorId: doctor?.id },
                });
                console.log('Fetched services:', res);
                setServices(res.data || []);
                if (res?.data.length > 0) {
                    setHasServices(true);
                }
            } catch (err) {
                console.error('Failed to load services', err);
            } finally {
                setLoading(false);
            }
        };

        if (doctor?.id) {
            fetchServices();
        }
    }, [doctor?.id,currentLanguage]);
    console.log(services);

    if (!loading && services.length === 0) {
        return null;    
    }

    const handleSeeMore = () => {
        navigate(`/${slug}/services`);
    };

    // Show services based on current path
    const displayedServices = isServicesPage 
        ? services 
        : services.slice(0, 6);
    
    // Only show "See More" button if not on services page and there are more than 6 services
    const hasMoreServices = !isServicesPage && services.length > 6;

    return (
        <section className="relative py-20">
            <div className="max-w-6xl mx-auto px-4">
                <div className="text-center mb-14 mx-auto flex flex-col items-center justify-center">
                    <h2 className="text-4xl font-bold text-center text-textPrimary flex items-center gap-3">
                        <MdOutlineMedicalServices className='text-3xl' /> 
                        {isServicesPage ? t('services.allOurServices') : t('services.ourServices')}
                    </h2>
                    <p className="mt-3 text-textPrimary/70 max-w-2xl mx-auto">
                        {isServicesPage 
                            ? t('services.allServicesDesc')
                            : t('services.ourServicesDesc')
                        }
                    </p>
                </div>

                {loading ? (
                    <div className="text-center text-lg text-gray-500">{t('common.loading')}</div>
                ) : (
                    <>
                        <motion.div
                            className={`grid gap-8 justify-center items-center ${
                                isServicesPage 
                                    ? "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4" 
                                    : "grid-cols-1 sm:grid-cols-2 md:grid-cols-3"
                            }`}
                            variants={containerVariants}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.3 }}
                        >
                            {displayedServices.map(({ id, title, description, photoID, price }) => (
                                <motion.div
                                    key={id}
                                    className="group p-8 rounded-3xl bg-primary/10 hover:bg-cardColor hover:shadow-xl transition duration-300 text-center"
                                    variants={cardVariants}
                                >
                                    <div className="relative w-20 h-20 mx-auto mb-5">
                                        <img
                                            src={imgBg}
                                            alt={t('services.backgroundShape')}
                                            className="absolute bottom-3 left-5 w-full h-full object-contain z-0 opacity-50"
                                        />
                                        <div className="relative w-full h-full rounded-full overflow-hidden border-2 border-primary/30 z-10">
                                            <img
                                                src={getImg(photoID)}
                                                alt={title}
                                                className="w-full h-full object-cover"
                                                onError={(e) =>
                                                    (e.target.src = 'https://via.placeholder.com/80x80?text=No+Image')
                                                }
                                            />
                                        </div>
                                    </div>

                                    <h3 className="text-xl font-semibold text-textPrimary">{title}</h3>
                                    <p className="mt-2 text-black/60 text-sm line-clamp-3">{description}</p>

                                    {price && <p className="mt-2 text-primary text-sm font-bold">
                                        {new Intl.NumberFormat(currentLanguage === 'ar' ? 'ar-EG' : 'en-US', {
                                            style: 'currency',
                                            currency: 'EGP',
                                            minimumFractionDigits: 0,
                                        }).format(price)}
                                    </p>}
                                </motion.div>
                            ))}
                        </motion.div>

                        {/* See More Button - only show if not on services page and there are more services */}
                        {hasMoreServices && (
                            <div className="text-center mt-12">
                                <button
                                    onClick={handleSeeMore}
                                    className="bg-primary hover:bg-secondary text-white font-semibold py-3 px-8 rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg"
                                >
                                    {t('services.seeMoreServices')}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </section>
    );
}